/**
 * Twilio Flex webchat (live-webchat iframe) session teardown. Mirrors
 * utils/intercom/intercom.ts — a per-integration reset hook called from the Vuex logout
 * action, so every logout path gets it.
 *
 * The SDK inside the iframe persists conversation state to localStorage on the webchat's
 * own origin, surviving iframe removal and a full page navigation — so without this the
 * next person on a shared machine could resume the previous user's conversation. The
 * iframe outlives Eap.vue, so this must work with no MessagePort open, hence a plain
 * postMessage to a pinned origin.
 *
 * Message shape mirrors live-webchat/src/contracts/embed.ts (WebchatLogout).
 */
import config from "../../config/constants";

// Keep in step with WEBCHAT_SCHEMA_VERSION in live-webchat/src/contracts/embed.ts.
// Single definition for up-spa — Eap.vue imports it for the handshake messages.
export const WEBCHAT_SCHEMA_VERSION = 1;

const WEBCHAT_IFRAME_ID = "twilio-webchat-iframe";

/**
 * Tell the embedded webchat its session has ended, then hide it.
 *
 * Best-effort: postMessage is async, so a logout that navigates immediately can cut the
 * frame off first. Hidden rather than removed, to keep it alive as long as possible.
 */
export const signOutWebchat = (): void => {
  const iframe = document.getElementById(WEBCHAT_IFRAME_ID);
  if (!iframe || !iframe.contentWindow || !config.webchat_url) return;

  try {
    const webchatOrigin = new URL(config.webchat_url).origin;
    iframe.contentWindow.postMessage(
      { type: "webchat:logout", v: WEBCHAT_SCHEMA_VERSION },
      webchatOrigin,
    );
  } catch {
    // Unparseable webchat_url — nothing to signal, and logout must not fail on it.
  }

  iframe.classList.remove("expanded");
  iframe.style.display = "none";
};
